import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Box } from '@mui/material';

import BillHistoryList from '../../../components/projectmng/bill_mng/BillHistoryList';
import AskDialog from '../../../components/common/dialog/AskDialog';
import * as taxApi from '../../../lib/api/taxbill';
import {
  listProjectBuyTaxbills,
  listProjectSalesTaxbills,
} from '../../../modules/projectModule/issue-taxbills';

/**
 * 매입·매출 계산서 등록 내역 (통합).
 *
 * 예전에는 매입 목록이 매입 탭과 매출 탭 양쪽에 걸려 있었고 매출 목록은 또 따로였다.
 * 한 프로젝트의 돈 흐름을 보려면 탭을 오가야 했다. 둘을 한 목록으로 합친다.
 */
const BillHistoryContainer = ({ project, isEdit }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { buyTaxbills, salesTaxbills } = useSelector(({ projectModule }) => ({
    buyTaxbills: projectModule.issueTaxbills.buyTaxbills,
    salesTaxbills: projectModule.issueTaxbills.salesTaxbills,
  }));

  const [target, setTarget] = useState(null);

  const reload = useCallback(() => {
    if (!project?.prjUuid) return;
    dispatch(listProjectBuyTaxbills(project.prjUuid));
    dispatch(listProjectSalesTaxbills(project.prjUuid));
  }, [dispatch, project]);

  useEffect(() => { reload(); }, [reload]);

  // 매입·매출을 한 줄씩 섞어 일자순으로 본다. 구분은 표의 첫 칸에서 나눈다.
  const rows = useMemo(() => {
    const buys = (buyTaxbills || []).map((r) => ({ ...r, invTyp: r.invTyp || '매입' }));
    const sales = (salesTaxbills || []).map((r) => ({ ...r, invTyp: r.invTyp || '매출' }));
    return [...buys, ...sales].sort((a, b) =>
      String(b.reqCompDt || '').localeCompare(String(a.reqCompDt || '')),
    );
  }, [buyTaxbills, salesTaxbills]);

  const handleDelete = async () => {
    const item = target;
    setTarget(null);
    if (!item) return;
    try {
      const res = await taxApi.removeIssuedTaxbills(item.invUuid);
      if (res.data) {
        enqueueSnackbar('매입계산서를 삭제했습니다.', { variant: 'success' });
        reload();
      }
    } catch (err) {
      const message =
        typeof err.response !== 'undefined' ? err.response.data.message : err.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <Box>
      <BillHistoryList rows={rows} isEdit={isEdit} onDelete={setTarget} />
      <AskDialog
        visible={Boolean(target)}
        title="매입 계산서 삭제"
        description={`"${target?.invTxt || ''}" 등록 내역을 삭제하시겠습니까?`}
        onConfirm={handleDelete}
        onCancle={() => setTarget(null)}
      />
    </Box>
  );
};

export default BillHistoryContainer;
